<template>
  <div @click.prevent.stop="openDialogIfApplicable">
    <!--<editor-fold desc="Dropdown autocomplete">-->
    <v-autocomplete
      v-if="!dialogOnly"
      v-model="valueDirty"
      :label="label"
      :single-line="!label"
      :items="items"
      :multiple="multiple"
      autocomplete="off"
      :item-text="itemText"
      :item-value="getValueProperty"
      :readonly="$vuetify.breakpoint.xsOnly || forceDialog"
      return-object
      v-bind="$attrs"
      :error-messages="errorMessagesComputed"
    >
      <template v-slot:item="{ item }">
        <slot
          name="dropdownSelectItem"
          :item="item"
        >
          <v-list-item-content>
            <v-list-item-title :class="{ 'grey--text': !item[itemText] }">
              {{ item['name'] || 'n/a' }}
            </v-list-item-title>
          </v-list-item-content>
        </slot>
      </template>
    </v-autocomplete>
    <!--</editor-fold>-->

    <!--<editor-fold desc="Dialog">-->
    <v-dialog
      v-model="dialogVisibleDirty"
      scrollable
      max-width="600px"
      :fullscreen="$vuetify.breakpoint.xsOnly"
      :transition="$vuetify.breakpoint.xsOnly ? 'dialog-bottom-transition' : 'scale-transition'"
      :persistent="$store.state.ui.alert.active"
    >
      <v-card>
        <!--<editor-fold desc="Dialog title">-->
        <v-card-title
          class="grey lighten-3"
          :class="{ 'pa-2': $vuetify.breakpoint.xsOnly, 'pa-4': $vuetify.breakpoint.smAndUp }"
        >
          <div class="flex-fill">
            <v-row
              :class="{ 'mb-1': $vuetify.breakpoint.xsOnly, 'mb-3': !$vuetify.breakpoint.xsOnly }"
              no-gutters
              align="center"
            >
              <v-col
                class="title"
              >
                <slot name="dialogTitle">
                  {{ dialogTitle }}
                </slot>
              </v-col>
              <v-col
                cols="auto"
                class="hidden-sm-and-up"
              >
                <v-btn
                  icon
                  text
                  class="ma-0"
                  @click="dialogVisibleDirty = false"
                >
                  <v-icon>close</v-icon>
                </v-btn>
              </v-col>
            </v-row>
            <slot name="filters" />
          </div>
        </v-card-title>
        <!--</editor-fold>-->
        <v-divider />
        <!--<editor-fold desc="Dialog content">-->
        <v-card-text class="pa-0">
          <div
            v-if="loading"
            class="d-flex justify-center align-center pa-12"
          >
            <v-progress-circular
              :size="50"
              :width="4"
              indeterminate
            />
          </div>
          <div
            v-else-if="items.length === 0"
            class="pa-6 text-center"
          >
            <slot name="noData" />
          </div>
          <v-list
            v-else
            two-line
            class="fill-height pa-0"
          >
            <v-virtual-scroll
              ref="virtualScroller"
              :items="items"
              item-height="72"
              class="bordered"
            >
              <template
                v-slot:default="{ item }"
                class="list--bordered"
              >
                <slot
                  name="dialogItem"
                  :item="item"
                  :selected="isSelected(item)"
                  :select-item="selectItem"
                />
              </template>
            </v-virtual-scroll>
          </v-list>
        </v-card-text>
        <!--</editor-fold>-->
        <v-divider />
        <!--<editor-fold desc="Dialog actions">-->
        <v-card-actions
          v-if="multiple"
          class="pa-4 shrink"
        >
          <v-spacer />
          <v-btn
            color="primary"
            @click="submitValue()"
          >
            <v-icon left>
              check
            </v-icon>
            Submit
          </v-btn>
        </v-card-actions>
        <!--</editor-fold>-->
      </v-card>
    </v-dialog>
    <!--</editor-fold>-->
  </div>
</template>

<script>
import { extractProps } from '@/components/helpers/inputProps'

export default {
    name: 'ModalSelect',
    props: {
      value: {
        type: [Array, Object],
        default: function () {
          return this.multiple ? [] : null
        }
      },
      multiple: {
        type: Boolean,
        default: false
      },
      label: {
        type: String,
        default: ''
      },
      forceDialog: {
        type: Boolean,
        default: false
      },
      dialogOnly: {
        type: Boolean,
        default: false
      },
      dialogVisible: {
        type: Boolean,
        default: false
      },
      loading: {
        type: Boolean,
        default: false
      },
      items: {
        type: Array,
        required: true
      },
      itemText: {
        type: String,
        required: true
      },
      dialogTitle: {
        type: String,
        required: true
      },
      isSameItem: {
        type: Function,
        default: function (item, otherItem) {
          return item.id === otherItem.id
        }
      },
      getValueProperty: {
        type: Function,
        default: function (item) {
          return item.id
        }
      },
      errorMessages: {
        type: Array,
        default: () => []
      }
    },
    computed: {
      valueDirty: {
        get: function () {
          return this.value
        },
        set: function (value) {
          this.$emit('input', value)
        }
      },
      dialogVisibleDirty: {
        get: function () {
          return this.dialogVisible
        },
        set: function (val) {
          this.$emit('update:dialogVisible', val)
        }
      },
      inputProps() {
        return extractProps(this)
      },
      errorMessagesComputed() {
        return this.errorMessages
      }
    },
    watch: {
      dialogVisibleDirty: function (value) {
        this.$nextTick(() => {
          this.$emit(value ? 'opened' : 'closed')
        })

        setTimeout(() => {
          if (this.$refs.virtualScroller) this.$refs.virtualScroller.onScroll()
        }, 0)
      }
    },
    mounted() {
      if (this.$refs.virtualScroller) {
        setTimeout(() => {
          this.$refs.virtualScroller.onScroll()
        }, 0)
      }
    },
    methods: {
      openDialogIfApplicable() {
        if (!this.$vuetify.breakpoint.xsOnly && !this.forceDialog && !this.dialogOnly) return

        this.dialogVisibleDirty = true
      },
      selectItem(item) {
        if (!this.multiple) {
          this.valueDirty = item
          this.submitValue()
          return
        }

        this.toggleSelected(item)
      },
      submitValue() {
        this.$emit('submit')
        // this.dialogVisibleDirty = false
      },
      toggleSelected(item) {
        if (!Array.isArray(this.valueDirty)) return

        const index = this.valueDirty.findIndex(i => this.isSameItem(i, item))

        if (index > -1) {
          this.valueDirty.splice(index, 1)
          return
        }

        this.valueDirty.push(item)
      },
      isSelected(item) {
        if (Array.isArray(this.valueDirty)) {
          return this.valueDirty.findIndex(i => this.isSameItem(i, item)) > -1
        }

        return this.valueDirty && this.isSameItem(this.valueDirty, item)
      }
    }
  }
</script>

<style scoped>

</style>
